import { proximityTier } from "./proximity.js";
import type { IPeer } from "./types.js";
import type { GeoHint, SegmentId } from "@p2p-hls/protocol";

/** Score penalty for a TURN-relayed peer (high latency). Kept below one proximity
 *  tier (1000) so a same-ASN relay can still beat a cross-ASN direct peer. */
const RELAY_PENALTY = 500;

/**
 * Holds the live set of connected peers and answers the scheduler's core
 * question: "who is the best peer to fetch THIS segment from right now?"
 *
 * Selection combines Tier 1 (network proximity from geo/ASN) with Tier 2
 * (measured RTT) and load. A same-ISP peer with a slightly higher RTT can still
 * beat a far peer because we bias by proximity tier first, then by RTT.
 */
export class PeerRegistry {
  private readonly peers = new Map<string, IPeer>();
  // Memoized proximity tier per peer. peer.geo is fixed for a connection, so the
  // tier only changes when OUR own geo changes (rare — tracker re-stamps it); we
  // invalidate the whole cache when that happens. Saves recomputing on every
  // pick()/peersWith() (called per segment AND per prefetch candidate).
  private readonly tierCache = new Map<string, 0 | 1 | 2 | 3>();
  private selfKey = "";

  constructor(private readonly selfGeo: () => GeoHint) {}

  add(peer: IPeer): void {
    this.peers.set(peer.id, peer);
  }

  remove(id: string): void {
    this.peers.delete(id);
    this.tierCache.delete(id);
  }

  /** Read self geo once per call; drop cached tiers if we've moved. */
  private freshSelf(): GeoHint {
    const self = this.selfGeo();
    const key = `${self.asn}|${self.region}|${self.country}`;
    if (key !== this.selfKey) {
      this.selfKey = key;
      this.tierCache.clear();
    }
    return self;
  }

  /** Memoized proximityTier(self, peer.geo). Call after freshSelf() this tick. */
  private tierOf(peer: IPeer, self: GeoHint): 0 | 1 | 2 | 3 {
    let t = this.tierCache.get(peer.id);
    if (t === undefined) {
      t = proximityTier(self, peer.geo);
      this.tierCache.set(peer.id, t);
    }
    return t;
  }

  get size(): number {
    return this.peers.size;
  }

  all(): IPeer[] {
    return [...this.peers.values()];
  }

  /**
   * Best candidate holding `seg` with spare capacity, or null. Lower score wins.
   * score = tier * TIER_WEIGHT + rtt + load penalty. Proximity dominates ties.
   */
  /** Up to `cap` peers that hold `seg` and have spare capacity, best-scored first. */
  peersWith(seg: SegmentId, cap: number, concurrency: number): IPeer[] {
    const self = this.freshSelf();
    const eligible: { peer: IPeer; score: number }[] = [];
    for (const peer of this.peers.values()) {
      if (!peer.has(seg) || peer.inflight() >= concurrency) continue;
      const rtt = peer.rtt();
      const rttEst = Number.isFinite(rtt) ? rtt : 150;
      const bwBonus = Math.min(400, peer.bandwidth() / 1024);
      const relayPenalty = peer.isRelayed?.() ? RELAY_PENALTY : 0;
      const score = this.tierOf(peer, self) * 1000 + rttEst - bwBonus + relayPenalty;
      eligible.push({ peer, score });
    }
    eligible.sort((a, b) => a.score - b.score);
    return eligible.slice(0, cap).map((e) => e.peer);
  }

  pick(seg: SegmentId, concurrency: number): IPeer | null {
    const self = this.freshSelf();
    let best: IPeer | null = null;
    let bestScore = Infinity;
    for (const peer of this.peers.values()) {
      if (!peer.has(seg)) continue;
      if (peer.inflight() >= concurrency) continue;
      const tier = this.tierOf(peer, self);
      const rtt = peer.rtt();
      // Unprobed peers (rtt=Infinity) get a neutral estimate so they're usable.
      const rttEst = Number.isFinite(rtt) ? rtt : 150;
      // Prefer higher throughput (bounded bonus) and spread load aggressively
      // across peers so the swarm's aggregate upload is used, not one peer's.
      const bwBonus = Math.min(400, peer.bandwidth() / 1024); // up to −400 for a fast peer
      const relayPenalty = peer.isRelayed?.() ? RELAY_PENALTY : 0; // TURN relay = high latency
      const score = tier * 1000 + rttEst + peer.inflight() * 200 - bwBonus + relayPenalty;
      if (score < bestScore) {
        bestScore = score;
        best = peer;
      }
    }
    return best;
  }
}
